import { useState, useRef, useEffect } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

interface City {
  id: number;
  nome: string;
  microrregiao: { mesorregiao: { UF: { sigla: string } } };
}

interface Props {
  city: string;
  state: string;
  onSelect: (city: string, state: string) => void;
  placeholder?: string;
}

export default function CityAutocomplete({ city, state, onSelect, placeholder = "Cidade *" }: Props) {
  const [query, setQuery] = useState(city ? `${city}${state ? ", " + state : ""}` : "");
  const [results, setResults] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (city && state) setQuery(`${city}, ${state}`);
    else if (city) setQuery(city);
  }, [city, state]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function search(q: string) {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
      const all: City[] = await res.json();
      const lower = q.toLowerCase();
      const filtered = all.filter((c) => c.nome.toLowerCase().startsWith(lower)).slice(0, 8);
      setResults(filtered);
      setOpen(filtered.length > 0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(v), 300);
  }

  function handleSelect(c: City) {
    const uf = c.microrregiao.mesorregiao.UF.sigla;
    setQuery(`${c.nome}, ${uf}`);
    setOpen(false);
    setResults([]);
    onSelect(c.nome, uf);
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-9"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-52 overflow-auto">
          {results.map((c) => {
            const uf = c.microrregiao.mesorregiao.UF.sigla;
            return (
              <li
                key={c.id}
                onMouseDown={() => handleSelect(c)}
                className="px-4 py-2 text-sm cursor-pointer hover:bg-secondary flex items-center gap-2"
              >
                <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                {c.nome}, {uf}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
